import React, { useEffect, useState } from "react";
import { ParentShadowWalletRechargePackInterface } from "../../interfaces/parent-shadow-wallet-recharge-pack.interface";
import { get, remove } from "../../redux/ApiHelper";
import { successToast, errorToast } from "../../utils/toast.util";
import { MdAdd, MdEdit, MdDeleteOutline } from "react-icons/md";
import ParentShadowWalletRechargePackModal from "./ParentShadowWalletRechargePackModal";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

const ParentShadowWalletRechargePackSettings = () => {
  const [packs, setPacks] = useState<ParentShadowWalletRechargePackInterface[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState<ParentShadowWalletRechargePackInterface | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  // Fetch all recharge packs
  const fetchPacks = async () => {
    try {
      setIsLoading(true);
      const response = await get("/admin/parent-shadow-wallet-recharge-pack/get-all");
      setPacks(response?.parentShadowWalletRechargePacks || []);
    } catch (error) {
      errorToast("Failed to fetch recharge packs");
      console.error("Error fetching packs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPacks();
  }, []);

  // Handle delete
  const handleDelete = async (packId: string) => {
    if (!window.confirm("Are you sure you want to delete this recharge pack?")) {
      return;
    }

    try {
      setIsLoading(true);
      await remove(`/admin/parent-shadow-wallet-recharge-pack/delete/${packId}`);
      successToast("Recharge pack deleted successfully");
      fetchPacks();
    } catch (error) {
      errorToast("Failed to delete recharge pack");
      console.error("Error deleting pack:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Open modal for create
  const openModal = () => {
    setIsEdit(false);
    setEditData(null);
    setIsModalOpen(true);
  };

  // Open modal for edit
  const handleEdit = (packData: ParentShadowWalletRechargePackInterface) => {
    setIsEdit(true);
    setEditData(packData);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditData(null);
    setIsEdit(false);
  };

  // Handle modal success (refresh list)
  const handleModalSuccess = () => {
    fetchPacks();
    closeModal();
  };

  return (
    <div className="bg-gray-900 text-yellow-500 flex items-center justify-center p-4 flex-grow min-h-screen">
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-6xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl font-semibold text-yellow-500">
            Parent Shadow Wallet Recharge Pack Settings
          </h1>
          <button
            onClick={openModal}
            className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-md hover:bg-yellow-400 transition font-medium flex items-center gap-2"
          >
            <MdAdd className="text-xl" />
            Add New Pack
          </button>
        </div>

        {isLoading && packs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            Loading recharge packs...
          </div>
        ) : packs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No recharge packs found. Click "Add New Pack" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Custom Table */}
            <table className="w-full border-collapse">
              {/* Table Header */}
              <thead>
                <tr className="bg-gray-700 border-b-2 border-yellow-500">
                  <th className="px-6 py-4 text-left text-yellow-500 font-semibold">
                    Coins
                  </th>
                  <th className="px-6 py-4 text-left text-yellow-500 font-semibold">
                    Price (INR)
                  </th>
                  <th className="px-6 py-4 text-center text-yellow-500 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              {/* Table Body */}
              <tbody>
                {packs.map((pack, index) => (
                  <tr
                    key={pack._id || index}
                    className={`border-b border-gray-700 hover:bg-gray-700/50 transition ${
                      index % 2 === 0 ? "bg-gray-800" : "bg-gray-800/50"
                    }`}
                  >
                    <td className="px-6 py-4 text-gray-300 font-medium">
                      {pack.coins}
                    </td>
                    <td className="px-6 py-4 text-gray-300 font-medium">
                      ₹{pack.price}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center items-center gap-3">
                        <button
                          onClick={() => handleEdit(pack)}
                          className="p-2 rounded-full bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 transition cursor-pointer"
                          title="Edit Pack"
                        >
                          <MdEdit className="text-lg" />
                        </button>
                        <button
                          onClick={() => pack._id && handleDelete(pack._id)}
                          className="p-2 rounded-full bg-red-500/20 text-red-500 hover:bg-red-500/30 transition cursor-pointer"
                          title="Delete Pack"
                        >
                          <MdDeleteOutline className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pack Count */}
            <div className="mt-4 text-sm text-gray-400 text-right">
              Total Packs: {packs.length}
            </div>
          </div>
        )}

        {/* Create/Update Modal */}
        <ParentShadowWalletRechargePackModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          isEdit={isEdit}
          editData={editData}
          onSuccess={handleModalSuccess}
        />
      </div>
    </div>
  );
};

export default ParentShadowWalletRechargePackSettings;

